import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Icon } from "../ui/icons";
import { addAliasToTerm } from "../lib/registryMerge";

// Surfaces the structurer Claude pass's proposed glossary terms for a
// meeting. Rendered inside the ReviewRail (studio layout).
//
// Fix 3 (2026-06-12): explicit contained editing — click-on-text removed;
// pencil IconButton per row opens a stacked-inputs edit panel constrained
// to the rail width. Save/Cancel are explicit actions below the inputs.

export interface ProposedTerm {
  term: string;
  notes?: string;
  source?: "notes" | "transcript" | "alias";
  canonical?: string;
}

function originOf(t: ProposedTerm): "notes" | "transcript" | "alias" {
  if (t.source === "alias") return "alias";
  return t.source === "transcript" ? "transcript" : "notes";
}
// Stable display order: notes-origin first, transcript-origin after; the
// ORIGINAL array index is retained so onEdit/onRemove/promote keep targeting
// the right row after reordering.
function orderedRows(terms: ProposedTerm[]): { t: ProposedTerm; i: number }[] {
  const withIdx = terms.map((t, i) => ({ t, i }));
  return [
    ...withIdx.filter((r) => originOf(r.t) === "notes"),
    ...withIdx.filter((r) => originOf(r.t) === "transcript"),
    ...withIdx.filter((r) => originOf(r.t) === "alias"),
  ];
}

interface EditDraft {
  term: string;
  notes: string;
}

interface Props {
  terms: ProposedTerm[];
  workspaceId?: string;
  // Meeting's current project assignment. When non-null, Promote writes
  // the term into THIS project. When null (unassigned), Promote is
  // disabled with a plain-language hint.
  meetingProjectId?: string | null;
  // Curation: when editable, each row gets a pencil button that opens
  // an explicit edit panel. Omit/false → read-only.
  editable?: boolean;
  onEdit?: (index: number, patch: Partial<ProposedTerm>) => void;
  onRemove?: (index: number) => void;
  /** Called after a dismiss or registry-removal so the parent can surface a
   *  toast. `undoFn` is provided only for dismiss (not for remove-from-registry). */
  onToast?: (message: string, undoFn?: () => void) => void;
  /** Called by the undo path to re-insert a previously dismissed term. */
  onReinsert?: (term: ProposedTerm) => void;
}

export function ProposedGlossaryCard({
  terms,
  workspaceId,
  meetingProjectId,
  editable,
  onEdit,
  onRemove,
  onToast,
  onReinsert,
}: Props) {
  // Which row (by original index) is in edit mode, and its working draft.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<EditDraft>({ term: "", notes: "" });

  // Cancel edit if the terms array changes structurally.
  useEffect(() => {
    setEditingIndex(null);
  }, [terms]);

  function openEdit(index: number) {
    const t = terms[index];
    setDraft({ term: t.term, notes: t.notes ?? "" });
    setEditingIndex(index);
  }

  function cancelEdit() {
    setEditingIndex(null);
  }

  function commitEdit(index: number) {
    const t = terms[index];
    const term = draft.term.trim();
    if (!term) return; // term is required
    const patch: Partial<ProposedTerm> = {};
    if (term !== (t.term || "").trim()) patch.term = term;
    const notes = draft.notes.trim();
    if (notes !== (t.notes ?? "").trim()) patch.notes = notes;
    if (Object.keys(patch).length > 0) onEdit?.(index, patch);
    setEditingIndex(null);
  }

  // ---------------------------------------------------------------------------
  // Registry state
  // ---------------------------------------------------------------------------

  const [registered, setRegistered] = useState<Set<string>>(new Set());
  const [registeredIds, setRegisteredIds] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [aliasPending, setAliasPending] = useState<Set<string>>(new Set());
  const [aliasDone, setAliasDone] = useState<Set<string>>(new Set());
  const [aliasMissing, setAliasMissing] = useState<Set<string>>(new Set());

  async function loadRegistered(ws: string) {
    setLoading(true);
    const { data } = await supabase
      .from("glossary_terms")
      .select("id, term, project_id")
      .eq("workspace_id", ws)
      .is("deleted_at", null);
    const set = new Set<string>();
    const ids = new Map<string, string>();
    for (const row of data ?? []) {
      if (!meetingProjectId || row.project_id !== meetingProjectId) continue;
      const term = (row.term || "").trim().toLowerCase();
      if (term) {
        set.add(term);
        if (row.id) ids.set(term, row.id);
      }
    }
    setRegistered(set);
    setRegisteredIds(ids);
    setLoading(false);
  }

  useEffect(() => {
    if (!workspaceId) return;
    loadRegistered(workspaceId);
  }, [workspaceId, meetingProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function promote(t: ProposedTerm) {
    if (!workspaceId) return;
    const key = t.term.trim().toLowerCase();
    if (!key || pending.has(key)) return;
    setPending((p) => new Set(p).add(key));
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
    const { error } = await supabase.from("glossary_terms").insert({
      workspace_id: workspaceId,
      project_id: meetingProjectId ?? null,
      term: t.term.trim(),
      notes: t.notes?.trim() || null,
      aliases: [],
    });
    setPending((p) => {
      const next = new Set(p);
      next.delete(key);
      return next;
    });
    if (error) {
      if (error.code === "23505") {
        setRegistered((r) => new Set(r).add(key));
      } else {
        setErrors((e) => ({ ...e, [key]: error.message }));
      }
      return;
    }
    setRegistered((r) => new Set(r).add(key));
    await loadRegistered(workspaceId);
  }

  async function removeFromRegistry(t: ProposedTerm) {
    if (!workspaceId || !meetingProjectId) return;
    const key = t.term.trim().toLowerCase();
    const rowId = registeredIds.get(key);
    const confirmed = window.confirm(
      `Remove "${t.term}" from this project's registry permanently? It won't be re-suggested.`,
    );
    if (!confirmed) return;

    let deleteError: { message: string } | null = null;
    if (rowId) {
      const { error } = await supabase.from("glossary_terms").delete().eq("id", rowId);
      deleteError = error;
    } else {
      const { error } = await supabase
        .from("glossary_terms")
        .delete()
        .eq("project_id", meetingProjectId)
        .eq("term", t.term.trim());
      deleteError = error;
    }

    if (deleteError) {
      onToast?.("Remove failed — please try again");
      return;
    }

    const { addIgnore } = await import("../lib/registryIgnores");
    await addIgnore(workspaceId, meetingProjectId, "glossary", t.term.trim());

    setRegistered((r) => { const next = new Set(r); next.delete(key); return next; });
    setRegisteredIds((m) => { const next = new Map(m); next.delete(key); return next; });

    onToast?.("Removed from registry");
  }

  async function addAlias(t: ProposedTerm) {
    if (!workspaceId || !meetingProjectId || !t.canonical) return;
    const key = t.term.trim().toLowerCase();
    if (aliasPending.has(key)) return;
    setAliasPending((p) => new Set(p).add(key));
    try {
      const { data: rows } = await supabase
        .from("glossary_terms")
        .select("id, term, aliases")
        .eq("workspace_id", workspaceId)
        .eq("project_id", meetingProjectId)
        .is("deleted_at", null);
      const canon = (rows ?? []).find(
        (r) => (r.term || "").trim().toLowerCase() === t.canonical!.trim().toLowerCase(),
      );
      if (!canon) {
        setAliasMissing((m) => new Set(m).add(key));
        return;
      }
      const merged = addAliasToTerm({ term: canon.term, aliases: canon.aliases ?? [] }, t.term);
      const { error } = await supabase
        .from("glossary_terms")
        .update({ aliases: merged.aliases })
        .eq("id", canon.id);
      if (error) {
        onToast?.("Couldn't add alias — please try again");
        return;
      }
      setAliasDone((d) => new Set(d).add(key));
      onToast?.(`Saved "${t.term}" as a spelling of "${t.canonical}"`);
    } finally {
      setAliasPending((p) => {
        const next = new Set(p);
        next.delete(key);
        return next;
      });
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 14, color: "var(--text-muted)" }}>
        Proposed glossary ({terms.length})
      </h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
        Terms from your context notes and mined from the conversation.{" "}
        {meetingProjectId
          ? "Use Promote to add to this project's glossary."
          : "Assign this meeting to a project to save terms to a registry."}
      </p>

      {orderedRows(terms).map(({ t, i }) => {
        const key = t.term.trim().toLowerCase();
        const inRegistry = registered.has(key);
        const isPending = pending.has(key);
        const rowError = errors[key];
        const origin = originOf(t);
        const isEditing = editingIndex === i;

        return (
          <div
            key={i}
            className="proposed-row"
            style={{ borderBottom: "1px solid var(--border)", padding: "8px 0" }}
          >
            {isEditing ? (
              /* ---- Edit mode: stacked inputs, full width, contained ---- */
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 0 }}>
                  Term
                  <input
                    autoFocus
                    type="text"
                    dir="auto"
                    value={draft.term}
                    onChange={(e) => setDraft((d) => ({ ...d, term: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      if (e.key === "Enter") { e.preventDefault(); commitEdit(i); }
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      boxSizing: "border-box",
                      marginTop: 2,
                      padding: "5px 8px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--accent)",
                      borderRadius: 4,
                      fontSize: 12,
                      fontFamily: "monospace",
                      fontWeight: 500,
                    }}
                  />
                </label>
                <label style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 0 }}>
                  Notes
                  <input
                    type="text"
                    dir="auto"
                    value={draft.notes}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      if (e.key === "Enter") { e.preventDefault(); commitEdit(i); }
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      boxSizing: "border-box",
                      marginTop: 2,
                      padding: "5px 8px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      borderRadius: 4,
                      fontSize: 12,
                      fontFamily: "inherit",
                    }}
                    placeholder="notes"
                  />
                </label>
                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => commitEdit(i)}
                    style={{ padding: "4px 12px", fontSize: 12, flex: "none" }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={cancelEdit}
                    style={{ padding: "4px 10px", fontSize: 12, flex: "none", color: "var(--text-muted)" }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* ---- View mode ---- */
              <div>
                {/* Term row: text + origin tag + pencil button + dismiss ✕ */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 4, marginBottom: 2 }}>
                  <span
                    dir="auto"
                    style={{ fontWeight: 500, fontSize: 12, fontFamily: "monospace", flex: 1, minWidth: 0, wordBreak: "break-word" }}
                  >
                    {t.term}
                  </span>
                  {editable && origin !== "alias" && (
                    <button
                      type="button"
                      onClick={() => openEdit(i)}
                      aria-label={`Edit ${t.term}`}
                      title="Edit"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-muted)",
                        opacity: 0.45,
                        padding: "0 2px",
                        fontSize: 12,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                      className="proposed-row-pencil"
                    >
                      <Icon name="edit" size={12} aria-hidden />
                    </button>
                  )}
                  {editable && (
                    <button
                      type="button"
                      className="row-remove"
                      onClick={() => {
                        const dismissed = t;
                        onRemove?.(i);
                        onToast?.("Suggestion dismissed", () => onReinsert?.(dismissed));
                      }}
                      aria-label={`Dismiss suggestion for ${t.term}`}
                      title="Dismiss suggestion"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-muted, #9ca3af)",
                        fontSize: 13,
                        padding: "0 2px",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Origin tag */}
                <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 2 }}>
                  {origin === "alias"
                    ? `variant of ${t.canonical ?? "a saved term"}`
                    : origin === "transcript"
                    ? "from the conversation"
                    : "from your notes"}
                </div>

                {/* Notes */}
                {t.notes && (
                  <div dir="auto" style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4, wordBreak: "break-word" }}>
                    {t.notes}
                  </div>
                )}

                {/* Actions: alias / promote / in-registry */}
                <div style={{ marginTop: 4 }}>
                  {origin === "alias" ? (
                    aliasDone.has(key) ? (
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>✓ Added as alias</span>
                    ) : aliasMissing.has(key) ? (
                      <span style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>
                        "{t.canonical}" is no longer in this glossary
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => void addAlias(t)}
                        disabled={aliasPending.has(key)}
                        style={{ padding: "2px 8px", fontSize: 11 }}
                        title={`Save "${t.term}" as another spelling of "${t.canonical}" so it is recognized next time`}
                      >
                        {aliasPending.has(key) ? "Adding…" : "Add as alias"}
                      </button>
                    )
                  ) : !workspaceId || loading ? null : inRegistry ? (
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>✓ In registry</span>
                      {editable && meetingProjectId && (
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => void removeFromRegistry(t)}
                          style={{ padding: "2px 8px", fontSize: 11, color: "var(--error, #f87171)", borderColor: "var(--error, #f87171)" }}
                          title={`Remove "${t.term}" from this project's registry`}
                        >
                          Remove from registry
                        </button>
                      )}
                    </div>
                  ) : !meetingProjectId ? (
                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>
                      Assign to a project first
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => promote(t)}
                      disabled={isPending}
                      style={{ padding: "2px 8px", fontSize: 11 }}
                      title="Add this term to this project's glossary so future meetings in this project recognize it"
                    >
                      {isPending ? "Adding…" : "Promote"}
                    </button>
                  )}
                </div>

                {rowError && (
                  <div className="error" style={{ fontSize: 11, marginTop: 2 }}>{rowError}</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
